import type { ButtonProps } from '@latitude/button';
import type { FooterProps as SharedFooterProps } from '@latitude/footer';
import { Footer as SharedFooter } from '@latitude/footer';
import type { FC } from 'react';

import type { ButtonAction } from '../';

/**
 * Props for the Footer component.
 */
export type FooterProps = {
  /**
   * The leading action button.
   */
  leadingAction?: ButtonAction;

  /**
   * The primary action button.
   */
  primaryAction?: ButtonAction;

  /**
   * The secondary action button.
   */
  secondaryAction?: ButtonAction;

  /**
   * The tertiary action button.
   */
  tertiaryAction?: ButtonAction;

  /**
   * Props forwarded straight to the shared `@latitude/footer`. Merged over the
   * flat actions above, so a call site can adopt it one action at a time, and
   * it reaches the slots the flat API never exposed (`description`,
   * `className`).
   *
   * Actions arriving this way still get the legacy `modal-button-action-*`
   * test IDs, so migrating props does not break a consumer's selectors. Those
   * IDs go separately, once every call site is off the flat props.
   */
  footerProps?: SharedFooterProps;
};

// Consumer tests across features/ and apps/ select footer buttons by this
// shape, including the literal "undefined" an action without a variant
// produces, so it is derived from the action rather than from what renders.
const withLegacyTestId = (
  action: ButtonAction | undefined,
  type: NonNullable<ButtonProps['type']>,
  slot: string,
) =>
  action && {
    'data-testid': `modal-button-action-${action.variant}-${type}-${slot}`,
    ...action,
  };

/**
 * Footer component of the modal. A thin adapter over the shared
 * `@latitude/footer`, which owns the button hierarchy and the rule that the
 * trailing group only renders alongside a primary action.
 */
export const Footer: FC<FooterProps> = ({
  leadingAction,
  primaryAction,
  secondaryAction,
  tertiaryAction,
  footerProps,
}) => (
  <SharedFooter
    data-testid="modal-footer"
    {...footerProps}
    leadingAction={withLegacyTestId(
      footerProps?.leadingAction ?? leadingAction,
      'ghost',
      'leading',
    )}
    primaryAction={withLegacyTestId(
      footerProps?.primaryAction ?? primaryAction,
      'filled',
      'primary',
    )}
    secondaryAction={withLegacyTestId(
      footerProps?.secondaryAction ?? secondaryAction,
      'outline',
      'secondary',
    )}
    tertiaryAction={withLegacyTestId(
      footerProps?.tertiaryAction ?? tertiaryAction,
      'ghost',
      'tertiary',
    )}
  />
);
